/* @flow */
import * as React from 'react'
require('../base.module.scss')
import * as cn from 'classnames'
import * as st from './Carousel.module.scss'
import { scrollTo } from '../../utils/scrolling'
import { extractClassNamesFromProps } from '../Common/extractClassNamesFromProps'

export interface CarouselProps {

  /** Should children elements be wrapped in LI's */
  wrapChildren?: boolean,

  /** Callback listener triggered when one of the navigational button is clicked */
  moveToListener?: (direction: Direction) => void,

  /** Flag, to set the carousel to infinitely loop. */
  isInfinite?: boolean,

  /** Should the scroll position be reset to 0 when items are added */
  resetOnChildUpdate?: boolean,

  /** Delay scroll in Milliseconds. This is used to combat a race condition in scroll events. */
  scrollDelayMilliseconds?: number,

  /** Scroll debounce delay in Milliseconds. */
  scrollDebounceMilliseconds?: number,

  /** Number of pixels before the beginning and end of the element, to hide the scroll buttons. */
  scrollTerminusBuffer?: number,

  /** Translation strings. */
  strings?: {
    leftButton: string,
    rightButton: string
  }

  scrollInterval?: number

  unusableVisibleWidth?: number,

  className?: string

  leftScrollButtonClassName?: string

  rightScrollButtonClassName?: string
}

enum Direction {
  left = 'left',
  right = 'right'
}

interface NavButtonProps {
  altText: string,
  onClick: (event: React.MouseEvent<HTMLElement>) => void,
  className: string,
  disabled?: boolean
}

interface State {
  elementWidth: number,
  carouselWidth: number,
  visibleWidth: number,
  minimumPosition: number,
  maximumPosition: number,
  showLoop: boolean,
  loopItemCount: number
  showLeftButton: boolean,
  showRightButton: boolean,
  debounce: any
}


const NavButton = ({ altText, onClick, className, disabled }: NavButtonProps) => {
  const classNames = cn(className, {
    [st.disabled]: disabled
  })
  return (
    <button onClick={onClick} className={classNames} aria-label={altText} />
  )
}

export class Carousel extends React.Component<CarouselProps, State> {
  static defaultProps = {
    wrapChildren: true as boolean,
    isInfinite: false as boolean,
    resetOnChildUpdate: false as boolean,
    scrollDelayMilliseconds: 10 as number,
    scrollDebounceMilliseconds: 100 as number,
    scrollTerminusBuffer: 3 as number,
    strings: {
      leftButton: 'Left' as string,
      rightButton: 'Right' as string
    } as Object,
    scrollInterval: 650 as number,
    unusableVisibleWidth: 0 as number
  }

  public readonly state: State = {
    elementWidth: 0,
    carouselWidth: 0,
    visibleWidth: 0,
    minimumPosition: 0,
    maximumPosition: 0,
    showLoop: false,
    loopItemCount: 0,
    showLeftButton: false,
    showRightButton: false,
    debounce: false
  }

  triggerStateUpdate = false
  blockScrollUntil = 0
  carouselElement?: HTMLElement
  io: IntersectionObserver
  firstChild: Element
  lastChild: Element

  carouselScrollTo(position: number, behavior: ScrollBehavior) {
    if (typeof this.carouselElement!.scrollTo !== 'undefined' &&
      'scrollBehavior' in document.documentElement.style) {
      this.carouselElement!.scrollTo({
        left: position,
        behavior
      })
    } else {
      scrollTo(this.carouselElement!, position, this.props.scrollInterval!)
    }
  }

  componentDidMount() {

    // Listening to the resize event, to recalculate scroll positions and widths.
    window.addEventListener('resize', this.resizeEvent)

    // If the carousel contains images (which shift size), add an on load event listener.
    const imgElement = this.carouselElement && this.carouselElement.querySelector('img')
    if (imgElement) {
      imgElement.addEventListener('load', this.imgLoadEvent)
    }

    const calculatedState = this.recalculateState()

    this.setState(
      calculatedState
    )

    this.observeChildren()

    this.carouselScrollTo(calculatedState.minimumPosition, 'auto')
  }

  setupIo() {
    if (!global.__SERVER__ && typeof IntersectionObserver === 'undefined') {
      require('intersection-observer')
    }
    const desiredIntersectionThreshold = 0.98
    if (typeof IntersectionObserver !== 'undefined')
      this.io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const doesIntersect = !(entry.intersectionRatio >= desiredIntersectionThreshold)
          if (!entry.target.nextSibling) {
            this.setState({ showRightButton: doesIntersect })
          }
          if (!entry.target.previousSibling) {
            this.setState({ showLeftButton: doesIntersect })
          }
        })
      }, {
        root: this.carouselElement,
        threshold: [desiredIntersectionThreshold]
      })
  }

  observeChildren() {
    if (!this.io)
      this.setupIo()

    if (this.io) {
      const newFirstChild = this.carouselElement.parentElement.querySelector('ul > li:first-child')
      const newLastChild = this.carouselElement.parentElement.querySelector('ul > li:last-child')
      if (this.lastChild && this.lastChild != newLastChild) {
        this.io.unobserve(this.lastChild)
      }
      this.io.observe(newLastChild)
      this.lastChild = newLastChild


      if (this.firstChild && this.firstChild != newFirstChild) {
        this.io.unobserve(this.firstChild)
      }
      this.io.observe(newFirstChild)
      this.firstChild = newFirstChild
    }
  }

  componentWillUnmount() {
    this.io && this.io.disconnect()
    window.removeEventListener('resize', this.resizeEvent)

    // If the carousel contains images (which shift size), add an on load event listener.
    const imgElement = this.carouselElement && this.carouselElement.querySelector('img')
    if (imgElement) {
      imgElement.removeEventListener('load', this.imgLoadEvent)
    }
  }

  componentDidUpdate() {
    if (this.triggerStateUpdate) {
      this.triggerStateUpdate = false
      this.setState(
        this.recalculateState()
      )
      if (this.props.resetOnChildUpdate) {
        this.moveTo(0)
      }
    }
    this.observeChildren()
  }

  componentWillReceiveProps(nextProps: CarouselProps & { children?: React.ReactNodeArray }) {
    this.triggerStateUpdate = false
    const currChildren = (this.props.children || []) as React.ReactNodeArray
    const nextChildren = nextProps.children || []
    if (nextChildren.length !== currChildren.length) {
      this.triggerStateUpdate = true
    } else {
      currChildren.forEach((value, index) => {
        if (nextChildren[index] && (nextChildren[index] as React.ReactElement<{}>).key !== (value as React.ReactElement<{}>).key) {
          this.triggerStateUpdate = true
        }
      })
    }
  }

  moveToClickEvent(direction: Direction) {
    if (this.props.moveToListener) {
      this.props.moveToListener(direction)
    }
    const newPosition = this.carouselElement!.scrollLeft + (
      direction === 'left' ? -this.state.visibleWidth : this.state.visibleWidth
    )
    const snappedPosition = newPosition - (newPosition % (this.state.elementWidth))
    this.moveTo(snappedPosition)
  }

  moveTo(newPosition: number) {
    let newPos = newPosition
    if (this.state.showLoop) {
      this.blockScrollUntil = newPosition
      if (newPosition < this.state.minimumPosition) {
        this.carouselElement!.scrollBy({
          left: this.state.carouselWidth,
          behavior: 'auto'
        })
        newPos += this.state.carouselWidth
      } else if (newPos > this.state.maximumPosition) {
        this.carouselElement!.scrollBy({
          left: -this.state.carouselWidth,
          behavior: 'auto'
        })
        newPos -= this.state.carouselWidth
      }
      this.blockScrollUntil = newPos
    }

    // Short time-out to deal with the bowser needing to recalculate positions
    setTimeout(() => {
      this.carouselScrollTo(newPosition, 'smooth')
    }, this.props.scrollDelayMilliseconds)
  }

  recalculateState() {
    const elementWidth = (this.carouselElement!.firstChild as HTMLElement).getBoundingClientRect().width,
      visibleWidth = this.carouselElement!.offsetWidth - this.props.unusableVisibleWidth!,
      carouselWidth = (React.Children.count(this.props.children) * elementWidth),
      showLoop = Boolean(this.props.isInfinite && (visibleWidth < carouselWidth)),
      loopItemCount = Math.ceil(visibleWidth / elementWidth)
    let minimumPosition = 0,
      maximumPosition = this.carouselElement!.scrollWidth

    if (showLoop) {
      minimumPosition = (this.carouselElement!.lastChild as HTMLElement).offsetWidth
      maximumPosition = (
        (this.carouselElement!.lastChild as HTMLElement).offsetWidth + carouselWidth + visibleWidth
      )
    }

    return {
      elementWidth,
      carouselWidth,
      visibleWidth,
      minimumPosition,
      maximumPosition,
      showLoop,
      loopItemCount
    }
  }

  imgLoadEvent = () => {
    this.setState(
      this.recalculateState()
    )
  }

  resizeEvent = () => {
    if (!this.state.debounce) {
      clearTimeout(this.state.debounce)
      this.state.debounce = setTimeout(() => {
        this.setState(
          this.recalculateState()
        )
        this.state.debounce = false
      }, this.props.scrollDebounceMilliseconds)
    }
  }

  scrollEvent = () => {
    const curPos = this.carouselElement!.scrollLeft,
      maxPos = this.state.maximumPosition,
      minPos = this.state.minimumPosition

    if (this.state.showLoop) {
      if (!this.blockScrollUntil || this.blockScrollUntil === curPos) {
        if (curPos < minPos) {
          this.carouselElement!.scrollBy({
            left: this.state.carouselWidth,
            behavior: 'auto'
          })
        } else if (curPos > (maxPos - this.state.visibleWidth)) {
          this.carouselElement!.scrollBy({
            left: -this.state.carouselWidth,
            behavior: 'auto'
          })
        }
        this.blockScrollUntil = 0
      }
    }
  }
  moveLeft = this.moveToClickEvent.bind(this, Direction.left)
  moveRight = this.moveToClickEvent.bind(this, Direction.right)

  render() {
    return (
      <div className={extractClassNamesFromProps(this.props, st.carousel)}>
        {this.state.showLeftButton &&
          <NavButton
            altText={this.props.strings!.leftButton}
            onClick={this.moveLeft}
            className={cn(st.left, this.props.leftScrollButtonClassName)}
          />
        }
        <ul className={st.container} ref={(elem) => {
          if (elem) {
            this.carouselElement = elem
          }
        }} onScroll={this.scrollEvent}>
          {this.state.showLoop && (this.props.children as React.ReactNodeArray)[(this.props.children as React.ReactNodeArray).length - 1]}
          {React.Children.map(this.props.children, child => (
            (this.props.wrapChildren ?
              <li className={st.card}>{child}</li> : <>{child}</>
            )
          ))}
          {this.state.showLoop && (this.props.children as React.ReactNodeArray).slice(0, this.state.loopItemCount)}
        </ul>
        {this.state.showRightButton &&
          <NavButton
            altText={this.props.strings!.rightButton}
            onClick={this.moveRight}
            className={cn(st.right, this.props.rightScrollButtonClassName)}
          />
        }
      </div>
    )
  }
}
